import type {UITask} from '@github-ui/agent-sessions/types/task'
import {DiffStats} from '@github-ui/diffs/DiffStats'
import {TaskLeadingVisual} from '@github-ui/agent-sessions/components/TaskLeadingVisual'
import {GitPullRequestIcon, VscodeIcon} from '@primer/octicons-react'
import {RelativeTime} from '@primer/react'
import {WithShimmerEffect} from '@github-ui/copilot-chat/components/WithShimmerEffect'
import {getReadableSessionState} from '@github-ui/agent-sessions/types/session'
import {resolveAgentProfileForTask} from '@github-ui/agent-sessions/agents/resolve-agent-profile'
import {generateTaskPathForRepo} from '@github-ui/agent-sessions/utils/generate-task-path'
import {useIntentDetectionEnabled} from '@github-ui/agent-sessions/contexts/IntentDetectionEnabledContext'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {clsx} from 'clsx'
import {useCallback} from 'react'
import {
  DashboardListItem,
  DashboardListItemActions,
  DashboardListItemDescription,
  DashboardListItemLeadingVisual,
  DashboardListItemTitle,
  DashboardListItemTrailingActionsMenu,
} from './DashboardListView'
import styles from './DashboardListView.module.css'
import {useVSCodeAgentSessionUrl} from '../hooks/useVSCodeAgentSessionUrl'
import {attemptOpenUrlWithFallback} from '../utils/attempt-open-url-with-fallback'
import {getTaskTitle} from '@github-ui/agent-sessions/utils/task-helpers'

interface AgentsListItemProps {
  task: UITask
  onTitleClick: () => void
  analyticsCategory?: string
}

export function AgentsListItem({task, onTitleClick, analyticsCategory = 'agents_list'}: AgentsListItemProps) {
  const navigate = useNavigate()
  const title = task.isOptimistic ? (task.problemStatement ?? 'Creating task') : getTaskTitle(task, task.pull?.title)
  // We know that nwo will be defined if isOptimistic is true, but TypeScript can't infer this
  // Use a fallback of empty string to satisfy the type checker
  const repoNameWithOwner = task.nwo ?? ''
  const prState = task.pull?.state ?? 'open'
  const prInMergeQueue = task.pull?.inMergeQueue ?? false

  const {isUsingIntentDetection} = useIntentDetectionEnabled()

  const taskUrl = generateTaskPathForRepo({
    repositoryNwo: task.nwo,
    pullRequestNumber: task.pull?.number,
    sessionId: task.latest_session_id,
    taskId: task.id,
    isUsingIntentDetection,
  })

  const renderDescriptionText = () => {
    if (task.isOptimistic) {
      return `${repoNameWithOwner} · Initializing session...`
    }

    const relativeTimeText = task.latest_session_state === 'completed' ? 'Completed' : 'Started'
    const revisionTextWithTrailingSeparator = task.session_count > 1 ? `${task.session_count} revisions ·` : ''
    const pullText = task.pull?.number ? ` #${task.pull.number}` : ''
    const currentLogText =
      prState === 'merged'
        ? 'Merged'
        : prState === 'closed'
          ? 'Closed'
          : prInMergeQueue
            ? 'Queued to merge'
            : getReadableSessionState(task.latest_session_state)

    const repoAndPullText = `${repoNameWithOwner}${pullText}`

    return (
      <>
        {repoAndPullText && (
          <>
            {repoAndPullText}
            <span> · </span>
          </>
        )}
        <span>
          {relativeTimeText} <RelativeTime format="relative" datetime={task.latest_session_updated_at} /> ·{' '}
          {revisionTextWithTrailingSeparator} {currentLogText}
        </span>
      </>
    )
  }

  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const handleTitleClick = useCallback(
    (e?: React.MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (!taskUrl || e?.metaKey || e?.ctrlKey) {
        return
      }
      e?.preventDefault()
      onTitleClick()
      navigate(taskUrl)
    },
    [taskUrl, onTitleClick, navigate],
  )

  const onClickViewPullRequest = useCallback(() => {
    sendClickAnalyticsEvent({category: analyticsCategory, action: 'click.view_pull_request_from_agent_task'})
  }, [sendClickAnalyticsEvent, analyticsCategory])

  const vsCodeAgentSessionUrl = useVSCodeAgentSessionUrl({
    repositoryNwo: repoNameWithOwner,
    globalId: task.resource_global_id,
    headRefName: task.pull?.head_ref_name,
  })

  const showOpenAgentSessionInVSCodeAction =
    isFeatureEnabled('dashboard_lists_open_agent_session_in_vscode') && vsCodeAgentSessionUrl
  const showActionsMenu =
    !task.isOptimistic && (task.pull || (showOpenAgentSessionInVSCodeAction && vsCodeAgentSessionUrl))

  const onClickOpenAgentSessionInVSCode = useCallback(() => {
    sendClickAnalyticsEvent({category: analyticsCategory, action: 'click.open_agent_session_in_vscode'})

    if (!vsCodeAgentSessionUrl) return

    attemptOpenUrlWithFallback(vsCodeAgentSessionUrl, repoNameWithOwner)
  }, [sendClickAnalyticsEvent, analyticsCategory, repoNameWithOwner, vsCodeAgentSessionUrl])

  const agentProfile = resolveAgentProfileForTask(task)
  const LeadingIcon = agentProfile.slots.LeadingIcon
  const TrailingVisual = agentProfile.slots.TrailingVisual
  const sessionFiltersEnabled = isFeatureEnabled('copilot_mission_control_session_filters')
  const EffectiveLeadingIcon = sessionFiltersEnabled ? null : LeadingIcon

  return (
    <DashboardListItem className={clsx(task.isOptimistic && styles.optimisticItem, styles.fadeIn)}>
      <DashboardListItemLeadingVisual>
        {EffectiveLeadingIcon ? (
          <div className={styles.mutedIcon}>
            <EffectiveLeadingIcon
              state={task.latest_session_state}
              prState={prState}
              prInMergeQueue={prInMergeQueue}
              isArchived={!!task.archived_at}
              isOptimistic={!!task.isOptimistic}
              staleAt={task.stale_at}
            />
          </div>
        ) : (
          <TaskLeadingVisual
            isOptimistic={!!task.isOptimistic}
            prState={prState}
            prInMergeQueue={prInMergeQueue}
            sessionState={task.latest_session_state}
            isArchived={!!task.archived_at}
          />
        )}
      </DashboardListItemLeadingVisual>
      {task.isOptimistic ? (
        <WithShimmerEffect className={styles.shimmeringTitle}>
          <DashboardListItemTitle as="div">{title}</DashboardListItemTitle>
        </WithShimmerEffect>
      ) : (
        <DashboardListItemTitle onClick={handleTitleClick} as="a" href={taskUrl}>
          {title}
        </DashboardListItemTitle>
      )}
      <DashboardListItemDescription>{renderDescriptionText()}</DashboardListItemDescription>
      <DashboardListItemActions>
        {task.latest_session_state !== 'in_progress' && task.pull?.pr_diff_summary && (
          <DiffStats
            linesAdded={task.pull.pr_diff_summary.lines_added}
            linesChanged={task.pull.pr_diff_summary.lines_changed}
            linesDeleted={task.pull.pr_diff_summary.lines_deleted}
            hideSquares
            className={styles.diffStats}
          />
        )}
        {TrailingVisual && (
          <div className={styles.trailingVisual}>
            <TrailingVisual
              state={task.latest_session_state}
              staleAt={task.stale_at}
              sharingStatus={task.sharing_status}
            />
          </div>
        )}
      </DashboardListItemActions>
      {showActionsMenu && (
        <DashboardListItemTrailingActionsMenu aria-label="Task actions">
          {task.pull && (
            <DashboardListItemTrailingActionsMenu.Item
              icon={GitPullRequestIcon}
              label="View pull request"
              href={task.pull.url}
              onClick={onClickViewPullRequest}
            />
          )}
          {showOpenAgentSessionInVSCodeAction && (
            <DashboardListItemTrailingActionsMenu.Item
              icon={VscodeIcon}
              label="Open session in VS Code"
              onClick={onClickOpenAgentSessionInVSCode}
            />
          )}
        </DashboardListItemTrailingActionsMenu>
      )}
    </DashboardListItem>
  )
}
